<template>
  <div class="container pull-up">
    <div class="row">
      <div class="col-md-12">
        <div class="card m-b-30">
          <div class="card-header">
            <div class="row align-items-center">
              <div class="col-md-6">
                <h4 class="card-title mb-0">Robust Item Upload</h4>
              </div>
              <div class="col-md-6 text-right">
                <el-button
                  type="primary"
                  icon="el-icon-download"
                  @click="downloadTemplate"
                  :loading="downloadingTemplate"
                >
                  Download Template
                </el-button>
              </div>
            </div>
          </div>
          <div class="card-body">
            <!-- Upload Area -->
            <div class="row mb-4">
              <div class="col-md-12">
                <div class="upload-area"
                     @click="initiateFileSelection"
                     @drop="handleDrop"
                     @dragover.prevent
                     @dragenter.prevent
                     :class="{ 'drag-over': isDragOver }"
                >
                  <div class="upload-content">
                    <i class="el-icon-upload upload-icon"></i>
                    <div class="upload-text">
                      <strong>Drop your Excel file here</strong><br>
                      or <em>click to browse</em>
                    </div>
                    <div class="upload-hint">
                      Supported formats: .xlsx, .xls (Max 10MB)
                    </div>
                  </div>
                  <input
                    ref="fileInput"
                    type="file"
                    accept=".xlsx,.xls"
                    @change="handleFileSelect"
                    style="display: none;"
                  >
                </div>
              </div>
            </div>

            <!-- File Info -->
            <div v-if="selectedFile" class="row mb-4">
              <div class="col-md-12">
                <div class="file-info">
                  <div class="file-details">
                    <i class="el-icon-document"></i>
                    <span class="file-name">{{ selectedFile.name }}</span>
                    <span class="file-size">({{ formatFileSize(selectedFile.size) }})</span>
                  </div>
                  <el-button
                    type="danger"
                    size="small"
                    icon="el-icon-delete"
                    @click="clearFile"
                  >
                    Remove
                  </el-button>
                </div>
              </div>
            </div>

            <!-- Upload Button -->
            <div v-if="selectedFile" class="row mb-4">
              <div class="col-md-12 text-center">
                <el-button
                  type="success"
                  size="large"
                  icon="el-icon-upload2"
                  @click="uploadFile"
                  :loading="uploading"
                  :disabled="!selectedFile"
                >
                  Upload Items
                </el-button>
              </div>
            </div>

            <!-- Instructions -->
            <div class="row">
              <div class="col-md-12">
                <div class="instructions">
                  <h5>Instructions:</h5>
                  <ul>
                    <li>Download the template to see the required format</li>
                    <li>Required fields: <strong>Name</strong>, <strong>Unit Price</strong></li>
                    <li>Optional fields: Code, Purchase Price, Description, Reorder Level, Category, Supplier ID, Barcode, Unit Type, Item Type, Sellable, Sell By</li>
                    <li>Categories will be created automatically if they don't exist</li>
                    <li>Items with duplicate names will be skipped</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Results Dialog -->
    <el-dialog
      title="Upload Results"
      :visible.sync="resultsDialogVisible"
      width="70%"
      :close-on-click-modal="false"
    >
      <div v-if="uploadResults">
        <div class="results-summary">
          <el-row :gutter="20">
            <el-col :span="6">
              <div class="stat-card success">
                <div class="stat-number">{{ uploadResults.statistics.created }}</div>
                <div class="stat-label">Created</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-card warning">
                <div class="stat-number">{{ uploadResults.statistics.skipped }}</div>
                <div class="stat-label">Skipped</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-card info">
                <div class="stat-number">{{ uploadResults.statistics.valid_rows }}</div>
                <div class="stat-label">Valid Rows</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-card danger">
                <div class="stat-number">{{ uploadResults.statistics.errors.length }}</div>
                <div class="stat-label">Errors</div>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- Errors -->
        <div v-if="uploadResults.statistics.errors.length > 0" class="errors-section">
          <h5>Errors:</h5>
          <div class="error-list">
            <div v-for="(error, index) in uploadResults.statistics.errors" :key="index" class="error-item">
              <i class="el-icon-warning"></i>
              {{ error }}
            </div>
          </div>
        </div>

        <!-- Warnings -->
        <div v-if="uploadResults.statistics.warnings.length > 0" class="warnings-section">
          <h5>Warnings:</h5>
          <div class="warning-list">
            <div v-for="(warning, index) in uploadResults.statistics.warnings" :key="index" class="warning-item">
              <i class="el-icon-info"></i>
              {{ warning }}
            </div>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="resultsDialogVisible = false">Close</el-button>
        <el-button type="primary" @click="goToItems">View Items</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'RobustUpload',
  data () {
    return {
      selectedFile: null,
      uploading: false,
      downloadingTemplate: false,
      isDragOver: false,
      resultsDialogVisible: false,
      uploadResults: null
    }
  },
  methods: {
    initiateFileSelection () {
      this.$refs.fileInput.click()
    },

    handleFileSelect (event) {
      const file = event.target.files[0]
      if (file) {
        this.validateAndSetFile(file)
      }
    },

    handleDrop (event) {
      this.isDragOver = false
      event.preventDefault()

      const files = event.dataTransfer.files
      if (files.length > 0) {
        this.validateAndSetFile(files[0])
      }
    },

    validateAndSetFile (file) {
      // Check file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ]

      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
        this.$notify.error('Please select a valid Excel file (.xlsx or .xls)')
        return
      }

      // Check file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.$notify.error('File size must be less than 10MB')
        return
      }

      this.selectedFile = file
    },

    clearFile () {
      this.selectedFile = null
      this.$refs.fileInput.value = ''
    },

    formatFileSize (bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    async downloadTemplate () {
      this.downloadingTemplate = true
      try {
        await this.$itemService.downloadTemplate()
        this.$notify.success('Template downloaded successfully')
      } catch (error) {
        this.$notify.error('Failed to download template')
        console.error('Download error:', error)
      } finally {
        this.downloadingTemplate = false
      }
    },

    async uploadFile () {
      if (!this.selectedFile) {
        this.$notify.error('Please select a file first')
        return
      }

      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('file', this.selectedFile)

        const response = await this.$itemService.robustUpload(formData)

        if (response.status === 'success') {
          this.uploadResults = response
          this.resultsDialogVisible = true
          this.$notify.success('Upload completed successfully')
        } else {
          this.$notify.error(response.message || 'Upload failed')
        }
      } catch (error) {
        console.error('Upload error:', error)
        this.$notify.error(error.response?.data?.message || 'Upload failed')
      } finally {
        this.uploading = false
      }
    },

    goToItems () {
      this.resultsDialogVisible = false
      this.$router.push({ name: 'items' })
    }
  },
  mounted () {
    this.$emit('parent-title', 'Robust Item Upload')
  }
}
</script>

<style scoped>
.upload-area {
  border: 3px dashed #d9d9d9;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #fafafa;
}

.upload-area:hover,
.upload-area.drag-over {
  border-color: #409eff;
  background-color: #f0f9ff;
}

.upload-content {
  pointer-events: none;
}

.upload-icon {
  font-size: 48px;
  color: #c0c4cc;
  margin-bottom: 16px;
}

.upload-text {
  font-size: 16px;
  color: #606266;
  margin-bottom: 8px;
}

.upload-text em {
  color: #409eff;
  font-style: normal;
}

.upload-hint {
  font-size: 12px;
  color: #909399;
}

.file-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f5f7fa;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

.file-details {
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-name {
  font-weight: 500;
  color: #303133;
}

.file-size {
  color: #909399;
  font-size: 12px;
}

.instructions {
  background-color: #f5f7fa;
  padding: 16px;
  border-radius: 4px;
  border-left: 4px solid #409eff;
}

.instructions h5 {
  margin-top: 0;
  margin-bottom: 12px;
  color: #303133;
}

.instructions ul {
  margin-bottom: 0;
  padding-left: 20px;
}

.instructions li {
  margin-bottom: 4px;
  color: #606266;
}

.results-summary {
  margin-bottom: 24px;
}

.stat-card {
  text-align: center;
  padding: 20px;
  border-radius: 8px;
  color: white;
}

.stat-card.success {
  background-color: #67c23a;
}

.stat-card.warning {
  background-color: #e6a23c;
}

.stat-card.info {
  background-color: #409eff;
}

.stat-card.danger {
  background-color: #f56c6c;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  opacity: 0.9;
}

.errors-section,
.warnings-section {
  margin-top: 20px;
}

.errors-section h5,
.warnings-section h5 {
  margin-bottom: 12px;
  color: #303133;
}

.error-list,
.warning-list {
  max-height: 200px;
  overflow-y: auto;
}

.error-item,
.warning-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 4px;
  border-radius: 4px;
  font-size: 14px;
}

.error-item {
  background-color: #fef0f0;
  color: #f56c6c;
  border: 1px solid #fbc4c4;
}

.warning-item {
  background-color: #fdf6ec;
  color: #e6a23c;
  border: 1px solid #f5dab1;
}

.error-item i,
.warning-item i {
  margin-top: 2px;
  flex-shrink: 0;
}
</style>
